import { useRouter } from 'next/router';
import usePermissions from '@/hooks/use-permissions';
import useSession from '@/hooks/use-session';
import { TenantFeatureGate } from '@/types/tenant';

// Routes (matched against `router.pathname`) where the global chat widget is suppressed.
// Everywhere else under PrivateLayout renders the widget. This is the inverse of the
// previous allowlist — opt new routes out by adding them here.
export const CHAT_WIDGET_DISABLED_ROUTES: string[] = [
  // Playbook create + callback sub-routes (the list + editor render the widget).
  '/playbooks/[id]/bank-linking-callback',
  '/playbooks/new',
  // Reporting dashboards.
  '/reporting/analysis',
  '/reporting/live-agent-activity',
  '/reporting/onboarding-metrics',
  // Internal-only surfaces (firm employees).
  '/internal/graph/[id]',
  '/internal/risk-ops',
  '/internal/tenants',
  '/internal/toolbox',
  // The theme studio's preview iframe renders this route; the studio page already has the widget.
  '/appearance-preview',
  // Full-bleed entity sub-views.
  '/entities/[fpId]/documents/[documentIdentifier]',
  '/users/[id]/onboardings/[onboarding_id]/trace',
  '/businesses/[id]/onboardings/[onboarding_id]/trace',
  // Authentication surfaces (PrivateLayout when navigated to while logged in).
  '/authentication/docs',
  '/authentication/link-sent',
  '/authentication/sign-in',
  '/authentication/sign-up',
  // User-flow / transitional surfaces.
  '/onboarding',
  // A walkthrough link's landing: its Start is the way into Percy, so a second entry competes with it.
  '/walkthroughs/[id]',
  '/switch-org',
  '/auth',
  '/logout',
  '/404',
];

/**
 * Whether the floating widget should render for the current tenant. Mirrors the
 * backend `POST /chat_sessions` gate (`chat_sessions/post.rs`): a user is allowed
 * when they're a firm employee, OR the tenant isn't sandbox-restricted, OR the
 * tenant has the `PercyChat` feature gate. Blocked tenants would only hit a 403
 * on their first message, so we hide the widget pre-emptively.
 */
export const useHasPercyChatAccess = (): boolean => {
  const {
    data: { user, org },
  } = useSession();
  const { isAuditor } = usePermissions();
  // Auditors are a restricted role that never has Percy, regardless of tenant gates.
  if (isAuditor) {
    return false;
  }
  return (
    !!user?.isFirmEmployee || !org?.isSandboxRestricted || !!org?.featureGates?.includes(TenantFeatureGate.PercyChat)
  );
};

/** Whether the global floating chat widget should render on the current route. */
const useChatWidgetEnabled = (): boolean => {
  const { pathname } = useRouter();
  const hasPercyChatAccess = useHasPercyChatAccess();
  if (CHAT_WIDGET_DISABLED_ROUTES.includes(pathname)) return false;
  if (!hasPercyChatAccess) return false;
  return true;
};

export default useChatWidgetEnabled;
